import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Link,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import UploadIcon from "@mui/icons-material/Upload";
import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionIcon from "@mui/icons-material/Description";
import { useIntl } from "react-intl";
import RevaText from "components/common/RevaText";
import { medicalRecordFileName } from "./player-form-helpers";

const MEDICAL_ACCEPT =
  ".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png,image/webp,image/gif";

const PlayerMedicalRecordUpload = ({
  player,
  expiresAt,
  onExpiresAtChange,
  onFileSelect,
  onRemove,
  uploading = false,
  removing = false,
  saveFirst = false,
}) => {
  const intl = useIntl();
  const recordUrl = player?.medical_record_url;
  const busy = uploading || removing;

  const handleChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || busy) return;
    onFileSelect?.(file);
  };

  return (
    <Stack spacing={1.5}>
      <RevaText variant="subtitle2">{intl.formatMessage({ id: "gestion.players.medicalRecord" })}</RevaText>
      {recordUrl ? (
        <Stack direction="row" alignItems="center" spacing={1}>
          <DescriptionIcon fontSize="small" color="action" />
          <Link href={recordUrl} target="_blank" rel="noopener noreferrer" underline="hover">
            {medicalRecordFileName(recordUrl) || intl.formatMessage({ id: "gestion.players.viewMedicalRecord" })}
          </Link>
          <Tooltip title={intl.formatMessage({ id: "gestion.players.removeMedicalRecord" })}>
            <span>
              <IconButton size="small" onClick={onRemove} disabled={busy || !player?.id}>
                {removing ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      ) : (
        <RevaText variant="body2" color="text.secondary">
          {intl.formatMessage({ id: "gestion.players.noMedicalRecord" })}
        </RevaText>
      )}
      <Box>
        <Button
          component="label"
          variant="outlined"
          size="small"
          startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
          disabled={busy}
        >
          {intl.formatMessage({
            id: recordUrl ? "gestion.players.replaceMedicalRecord" : "gestion.players.uploadMedicalRecord",
          })}
          <input hidden type="file" accept={MEDICAL_ACCEPT} onChange={handleChange} />
        </Button>
      </Box>
      <RevaText variant="caption" color="text.secondary">
        {intl.formatMessage({
          id: saveFirst ? "gestion.players.medicalSaveFirst" : "gestion.players.medicalHint",
        })}
      </RevaText>
      <TextField
        label={intl.formatMessage({ id: "gestion.players.medicalRecordExp" })}
        type="date"
        value={expiresAt}
        onChange={(event) => onExpiresAtChange?.(event.target.value)}
        InputLabelProps={{ shrink: true }}
        fullWidth
      />
    </Stack>
  );
};

export default PlayerMedicalRecordUpload;
